import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';
import { blogPosts, getBlogPost, getRelatedPosts } from '../data/blog';
import {
  CalendarIcon,
  ClockIcon,
  TagIcon,
  UserIcon,
  ArrowLeftIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogPost(slug);
  const relatedPosts = getRelatedPosts(post);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [slug]);

  if (!post) {
    return (
      <div className="section-padding">
        <div className="container-custom text-center">
          <h2 className="text-2xl font-bold mb-4">Blog Post Not Found</h2>
          <Link to="/blog" className="text-naqd-primary dark:text-naqd-green hover:underline">
            <ArrowLeftIcon className="w-4 h-4 inline mr-1" />Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.description,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback for browsers without native sharing
      const url = window.location.href;
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  // Helper to parse inline markdown (bold, italic)
  const parseInlineMarkdown = (text) => {
    if (!text) return null;

    // Split by bold markers
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index} className="font-bold text-naqd-primary dark:text-white">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Split content into paragraphs and format properly
  const formatContent = (content) => {
    if (!content) return '';

    // Split by double newlines to create paragraphs/blocks
    const blocks = content.split('\n\n').filter(p => p.trim());

    return blocks.map((block, index) => {
      const trimmed = block.trim();

      // Handle Tables
      if (trimmed.startsWith('|')) {
        const rows = trimmed.split('\n').filter(row => row.trim());
        const headers = rows[0].split('|').filter(cell => cell.trim()).map(cell => cell.trim());
        const alignRow = rows[1]; // Skip alignment row for now
        const dataRows = rows.slice(2);

        return (
          <div key={index} className="overflow-x-auto mb-8">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-naqd-light-bg dark:bg-naqd-dark-bg border-b border-naqd-light-border dark:border-naqd-dark-border">
                  {headers.map((header, i) => (
                    <th key={i} className="p-4 font-semibold text-naqd-primary dark:text-naqd-green">
                      {parseInlineMarkdown(header)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataRows.map((row, i) => {
                  const cells = row.split('|').filter(cell => cell.trim()).map(cell => cell.trim());
                  return (
                    <tr key={i} className="border-b border-naqd-light-border dark:border-naqd-dark-border hover:bg-naqd-light-bg/50 dark:hover:bg-naqd-dark-bg/50">
                      {cells.map((cell, j) => (
                        <td key={j} className="p-4 text-naqd-light-text dark:text-naqd-dark-text">
                          {parseInlineMarkdown(cell)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }

      // Handle bullet points
      if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('* ')) {
        const items = trimmed.split('\n').filter(item => item.trim());
        return (
          <ul key={index} className="list-disc list-inside space-y-2 mb-6 text-naqd-light-text dark:text-naqd-dark-text ml-4">
            {items.map((item, i) => (
              <li key={i} className="text-lg leading-relaxed pl-2">
                {parseInlineMarkdown(item.replace(/^•\s*|-\s*|\*\s*/, ''))}
              </li>
            ))}
          </ul>
        );
      }

      // Handle numbered lists
      if (/^\d+\./.test(trimmed)) {
        const items = trimmed.split('\n').filter(item => item.trim());
        return (
          <ol key={index} className="list-decimal list-inside space-y-2 mb-6 text-naqd-light-text dark:text-naqd-dark-text ml-4">
            {items.map((item, i) => (
              <li key={i} className="text-lg leading-relaxed pl-2">
                {parseInlineMarkdown(item.replace(/^\d+\.\s*/, ''))}
              </li>
            ))}
          </ol>
        );
      }

      // Handle headings
      if (trimmed.startsWith('#')) {
        const level = trimmed.match(/^#+/)[0].length;
        const text = trimmed.replace(/^#+\s*/, '');
        const Tag = `h${Math.min(level + 1, 6)}`; // Shift levels down (h1 -> h2) to fit article structure

        const sizes = {
          2: 'text-3xl',
          3: 'text-2xl',
          4: 'text-xl',
          5: 'text-lg',
          6: 'text-base'
        };

        return (
          <Tag key={index} className={`${sizes[Math.min(level + 1, 6)]} font-bold mt-10 mb-4 text-naqd-primary dark:text-white`}>
            {parseInlineMarkdown(text)}
          </Tag>
        );
      }

      // Regular paragraphs
      return (
        <p key={index} className="text-lg leading-relaxed mb-6 text-naqd-light-text dark:text-naqd-dark-text">
          {parseInlineMarkdown(trimmed)}
        </p>
      );
    });
  };

  return (
    <div>
      <MetaTags
        title={post.title}
        description={post.description}
        keywords={post.keywords}
      />

      {/* Article Header */}
      <section className="section-padding bg-naqd-light-surface dark:bg-naqd-dark-surface">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center text-naqd-primary dark:text-naqd-green hover:text-naqd-primary/80 dark:hover:text-naqd-green/80 mb-6 animate-on-scroll"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-1" />
              Back to Blog
            </Link>

            <article className="animate-on-scroll">
              <header className="mb-8">
                <div className="flex flex-wrap items-center gap-4 text-sm text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-4">
                  <span className="flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    {post.date}
                  </span>
                  <span className="flex items-center">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    {post.readTime}
                  </span>
                  <span className="flex items-center">
                    <UserIcon className="w-4 h-4 mr-1" />
                    {post.author}
                  </span>
                  <span className="inline-block bg-naqd-primary/10 dark:bg-naqd-green/20 text-naqd-primary dark:text-naqd-green text-sm px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl font-display font-bold mb-4 leading-tight text-naqd-light-text dark:text-naqd-dark-text">
                  {post.title}
                </h1>

                <p className="text-xl text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-6">
                  {post.description}
                </p>

                <div className="flex items-center space-x-4 pb-4 border-b border-naqd-light-border dark:border-naqd-dark-border">
                  <button
                    onClick={handleShare}
                    className="flex items-center space-x-2 text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary hover:text-naqd-primary dark:hover:text-naqd-green transition-colors"
                  >
                    <ShareIcon className="w-4 h-4" />
                    <span className="text-sm">Share</span>
                  </button>
                </div>
              </header>

              <div className="prose prose-lg max-w-none dark:prose-invert">
                <div className="text-naqd-light-text dark:text-naqd-dark-text leading-relaxed">
                  {formatContent(post.content)}
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="section-padding bg-naqd-light-bg dark:bg-naqd-dark-bg">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-8 text-naqd-light-text dark:text-naqd-dark-text">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="card group"
                  >
                    <h3 className="font-semibold group-hover:text-naqd-primary dark:group-hover:text-naqd-green transition-colors mb-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                      {relatedPost.description.substring(0, 100)}...
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;